/**
 * <TutorLiveOverlays> — ce qui se pose par-dessus le live du masteur (template).
 *
 * Les feuilles et les boîtes de `<TutorLivePage>`, réunies pour que la page ne fasse que disposer ses
 * zones :
 *  - « Plus » (`more`, une feuille d'actions) : les gestes de groupe et d'aide, « Tous en Concentré » en
 *    tête (`onAllFocused`, venu du bandeau au round 3) ;
 *  - la fin (`<TutorLiveEndDialog>`) ;
 *  - un dialogue d'aujourd'hui à la fois (`dialog`) : séance individuelle, Sam en cours, présentation à
 *    interrompre, plusieurs onglets, le live suivant qui commence, un fichier envoyé dans le chat, les
 *    permissions refusées — et tout autre dialogue de l'activité, ses mots écrits par l'écran (`custom` :
 *    archiver, « Non autorisé », « Impossible », « Valider l'activité », archiver pendant un échange avec le champion) ;
 *  - une feuille de l'écran (`sheet`, une fente : les Ressources d'une activité, une pièce du chat) ;
 *  - « Un problème ? » (`<LiveHelpSheet viewer="tutor">`), le résumé de Sam (`<TutorSamSummarySheet>`),
 *    « Signaler » une question du quiz (`<QuizQuestionSignalSheet>`) ;
 *  - l'accusé ou l'échec d'un geste, au-dessus de la barre (`<Snackbar>`).
 *
 * Contrôlé : chaque surface ouverte arrive en prop, chaque geste repart.
 */
import React, { useMemo } from "react"

import { type IconName } from "../../atoms/icons/Icon/iconRegistry"
import { ActionSheet } from "../../molecules/ActionSheet"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { DeviceCheckGuidance, type UIGuidanceLine } from "../../molecules/DeviceCheckGuidance"
import { Snackbar } from "../../molecules/Snackbar"
import { TutorLiveEndDialog, type TutorLiveEndDialogProps } from "../../molecules/TutorLiveEndDialog"
import { QuizQuestionSignalSheet, type QuizQuestionSignalSheetProps } from "../../organisms/QuizQuestionSignalSheet"
import { LiveHelpSheet, type LiveHelpSheetProps } from "../LiveHelpSheet"
import { TutorSamSummarySheet, type TutorSamSummarySheetProps } from "../TutorSamSummarySheet"

/** Les dialogues d'aujourd'hui, un à la fois. */
export type UITutorLiveDialog =
    /** Séance individuelle : le son reste ouvert entre le masteur et l'élève. */
    | { kind: "individual_sound" }
    /** L'élève est en point d'avancement avec Sam : l'interrompre pour dialoguer ? */
    | { kind: "interrupt_sam"; studentName: string }
    /** Le partage d'écran s'arrête si on quitte cette vue. */
    | { kind: "interrupt_presentation" }
    /** Plusieurs onglets ouverts sur la même séance (le web). */
    | { kind: "keep_tab" }
    /** Le live suivant a commencé : l'ancien `TutorOverrunDialog`, ses phrases écrites par l'adaptateur. */
    | { kind: "overrun"; title: string; lines: string[] }
    /** Le masteur envoie un fichier dans le chat : « Crée une activité » (l'ancien `CommonChat`). */
    | { kind: "chat_attachment"; media: "photo" | "image" | "file" }
    /**
     * La caméra ou le micro refusés au navigateur (l'ancien `PermissionsManager`) : où les rendre, pour
     * le navigateur de la personne (les pas écrits par l'adaptateur), « Actualiser » / « Ignorer ».
     */
    | { kind: "permissions"; steps: UIGuidanceLine[] }
    /** Tout autre dialogue, ses mots écrits par l'écran (ceux de la fiche d'une activité, au tutoiement). */
    | { kind: "custom"; title: string; message: string; confirmLabel: string; cancelLabel?: string; danger?: boolean }

/** Un accusé ou un échec, en bas de l'écran : « Impossible de basculer Karim en Dialogue ». */
export type UITutorLiveSnackbar = { message: string; tone: "success" | "danger" | "info" }

/** Une ligne de « Plus ». */
export type TutorLiveMoreAction = { id: string; label: string; icon: IconName }

export type TutorLiveOverlaysProps = {
    more: {
        open?: boolean
        onClose: () => void
        /** Les gestes de groupe et d'aide, dans leur ordre. */
        actions: TutorLiveMoreAction[]
        onAction: (actionId: string) => void
        /** « Tous en Concentré », en tête de « Plus ». Absent (une séance individuelle) : rien. */
        onAllFocused?: () => void
        /** Tous les présents sont déjà en Concentré : la ligne se tait. Défaut `false`. */
        allFocused?: boolean
        /** Une séance d'essai : « Prendre RDV avec un coordinateur » (au bandeau jusqu'au round 3). */
        onBookCall?: () => void
    }
    end: Omit<TutorLiveEndDialogProps, "testID">
    dialog?: { value: UITutorLiveDialog; onConfirm: () => void; onCancel?: () => void; onDismiss?: () => void } | null
    /** « Un problème ? » du masteur : il ne choisit que ses problèmes, ses lignes suivent ses gestes. */
    help?: Omit<LiveHelpSheetProps, "viewer"> | null
    samSummary?: TutorSamSummarySheetProps | null
    quizSignal?: QuizQuestionSignalSheetProps | null
    snackbar?: { value: UITutorLiveSnackbar; onDismiss: () => void } | null
    /** Une feuille de l'écran, posée par-dessus (une fente). */
    sheet?: React.ReactNode
}

const TEXTS = {
    more: "Plus",
    individualTitle: "Séance individuelle",
    individualMessage: "En séance individuelle, le son doit rester ouvert entre toi et l'élève.",
    ok: "OK",
    samTitle: (name: string) => `${name} est en plein point d'avancement avec Sam`,
    samMessage: "Souhaites-tu interrompre la conversation pour dialoguer avec l'élève ?",
    samConfirm: "Oui, interrompre",
    no: "Non",
    presentationTitle: "Interrompre la présentation ?",
    presentationMessage: "Le partage d'écran sera arrêté si tu quittes cette vue.",
    presentationConfirm: "Interrompre",
    stay: "Rester",
    keepTabTitle: "Plusieurs onglets détectés",
    keepTabMessage:
        "Tu as plusieurs onglets ouverts sur la même séance. Garde celui-ci : les autres se fermeront automatiquement.",
    keepTabConfirm: "Garder cet onglet",
    overrunConfirm: "J'ai compris",
    attachmentTitle: "Crée plutôt une activité",
    attachmentMessage: (media: "photo" | "image" | "file") =>
        `Si c'est un travail à faire, une activité se suit plus facilement. Tu peux aussi envoyer ${ATTACHMENT_WORDS[media]} comme ça.`,
    attachmentConfirm: "Créer l'activité",
    attachmentSendAnyway: "Envoyer",
    permissionsTitle: "Autorise ta caméra et ton micro",
    permissionsMessage:
        "Active les permissions de la caméra et du micro dans les paramètres de ton navigateur, puis actualise la page.",
    permissionsRefresh: "Actualiser",
    permissionsIgnore: "Ignorer",
}

const ATTACHMENT_WORDS = { photo: "la photo", image: "l'image", file: "le fichier" } as const

/** « Tous en Concentré », venu du bandeau au round 3. */
const ALL_FOCUSED_ACTION: TutorLiveMoreAction = { id: "all_focused", label: "Tous en Concentré", icon: "volume-mute" }
/** « Prendre RDV avec un coordinateur », une séance d'essai : la dernière ligne de « Plus ». */
const BOOK_CALL_ACTION: TutorLiveMoreAction = {
    id: "book_call",
    label: "Prendre RDV avec un coordinateur",
    icon: "calendar-plus",
}

/** L'accusé se pose au-dessus de la barre du bas. */
const SNACKBAR_ABOVE_BAR = 88

const noop = () => {}

export function TutorLiveOverlays(props: TutorLiveOverlaysProps) {
    const { more, end, dialog, help, samSummary, quizSignal, snackbar, sheet } = props
    const { actions, onAction, onAllFocused, allFocused = false, onBookCall } = more

    const moreItems = useMemo(
        () => [
            ...(onAllFocused != null
                ? [
                      {
                          icon: ALL_FOCUSED_ACTION.icon,
                          label: ALL_FOCUSED_ACTION.label,
                          onPress: onAllFocused,
                          disabled: allFocused,
                      },
                  ]
                : []),
            ...actions.map((action) => ({
                icon: action.icon,
                label: action.label,
                onPress: () => onAction(action.id),
            })),
            ...(onBookCall != null
                ? [{ icon: BOOK_CALL_ACTION.icon, label: BOOK_CALL_ACTION.label, onPress: onBookCall }]
                : []),
        ],
        [actions, onAction, onAllFocused, allFocused, onBookCall]
    )

    const confirm = dialog != null ? dialogCopy(dialog.value) : null

    return (
        <>
            <ActionSheet visible={more.open === true} onClose={more.onClose} title={TEXTS.more} items={moreItems} />
            <TutorLiveEndDialog {...end} />
            {dialog != null && confirm != null && (
                <ConfirmDialog
                    visible
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel={confirm.confirmLabel}
                    cancelLabel={confirm.cancelLabel}
                    hideCancel={confirm.cancelLabel == null}
                    danger={confirm.danger}
                    onConfirm={dialog.onConfirm}
                    onCancel={dialog.onCancel ?? dialog.onConfirm}
                    onDismiss={dialog.onDismiss}
                >
                    {confirm.steps != null && <DeviceCheckGuidance lines={confirm.steps} />}
                </ConfirmDialog>
            )}
            {help != null && <LiveHelpSheet {...help} viewer="tutor" />}
            {samSummary != null && <TutorSamSummarySheet {...samSummary} />}
            {quizSignal != null && <QuizQuestionSignalSheet {...quizSignal} />}
            {sheet}
            <Snackbar
                visible={snackbar != null}
                message={snackbar?.value.message ?? ""}
                tone={snackbar?.value.tone}
                onDismiss={snackbar?.onDismiss ?? noop}
                bottomOffset={SNACKBAR_ABOVE_BAR}
            />
        </>
    )
}

type DialogCopy = {
    title: string
    message: string
    confirmLabel: string
    cancelLabel?: string
    danger: boolean
    steps?: UIGuidanceLine[]
}

/** Les mots de chaque dialogue d'aujourd'hui. */
function dialogCopy(dialog: UITutorLiveDialog): DialogCopy {
    switch (dialog.kind) {
        case "custom":
            return {
                title: dialog.title,
                message: dialog.message,
                confirmLabel: dialog.confirmLabel,
                cancelLabel: dialog.cancelLabel,
                danger: dialog.danger === true,
            }
        case "individual_sound":
            return {
                title: TEXTS.individualTitle,
                message: TEXTS.individualMessage,
                confirmLabel: TEXTS.ok,
                danger: false,
            }
        case "interrupt_sam":
            return {
                title: TEXTS.samTitle(dialog.studentName),
                message: TEXTS.samMessage,
                confirmLabel: TEXTS.samConfirm,
                cancelLabel: TEXTS.no,
                danger: false,
            }
        case "interrupt_presentation":
            return {
                title: TEXTS.presentationTitle,
                message: TEXTS.presentationMessage,
                confirmLabel: TEXTS.presentationConfirm,
                cancelLabel: TEXTS.stay,
                danger: true,
            }
        case "keep_tab":
            return {
                title: TEXTS.keepTabTitle,
                message: TEXTS.keepTabMessage,
                confirmLabel: TEXTS.keepTabConfirm,
                danger: false,
            }
        case "chat_attachment":
            return {
                title: TEXTS.attachmentTitle,
                message: TEXTS.attachmentMessage(dialog.media),
                confirmLabel: TEXTS.attachmentConfirm,
                cancelLabel: TEXTS.attachmentSendAnyway,
                danger: false,
            }
        case "permissions":
            return {
                title: TEXTS.permissionsTitle,
                message: TEXTS.permissionsMessage,
                confirmLabel: TEXTS.permissionsRefresh,
                cancelLabel: TEXTS.permissionsIgnore,
                danger: false,
                steps: dialog.steps,
            }
        case "overrun":
            return {
                title: dialog.title,
                message: dialog.lines.join(" "),
                confirmLabel: TEXTS.overrunConfirm,
                danger: false,
            }
    }
}
